import React from 'react';
import { Download, Package } from 'lucide-react';
import { PRODUCTS } from '../constants';
import { Product } from '../types';

const groupByCategory = (products: Product[]): Record<string, Product[]> => {
  const groups: Record<string, Product[]> = {};
  products.forEach((p) => {
    if (!groups[p.category]) groups[p.category] = [];
    groups[p.category].push(p);
  });
  // Within each category, sort by blade length DESCENDING (biggest first).
  // Retailers see the larger workhorse blade as the lead variant.
  Object.keys(groups).forEach((cat) => {
    groups[cat].sort((a, b) => (b.bladeLength ?? 0) - (a.bladeLength ?? 0));
  });
  return groups;
};

const CatalogPage: React.FC = () => {
  const grouped = groupByCategory(PRODUCTS);

  const handleDownloadPDF = () => {
    const a = document.createElement('a');
    a.href = '/the-machete-man-catalog.pdf';
    a.download = 'The-Machete-Man-Catalog.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="bg-paper min-h-screen">
      {/* Header — compressed on mobile so the first product card appears within the
          first viewport. Desktop keeps the editorial scale. */}
      <header className="bg-forge text-paper py-8 sm:py-12 lg:py-24 px-6 lg:px-10 relative print:bg-white print:text-black print:py-8">
        <div className="max-w-editorial mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-8 mb-4 md:mb-10">
            <div>
              <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-3 md:mb-5 print:text-ember">
                — Product guide
              </p>
              <h1 className="font-editorial italic font-light text-3xl sm:text-4xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-2 md:mb-3 print:text-black">
                Product guide.
              </h1>
              <p className="text-sm md:text-base text-paper/70 print:text-gray-600">
                In-stock collection · SAE 1075 high carbon steel
              </p>
            </div>

            <button
              onClick={handleDownloadPDF}
              className="print:hidden inline-flex items-center gap-2 bg-ember hover:bg-ember-deep text-paper-warm px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-medium transition-colors duration-150 ease-editorial self-start md:self-auto"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download PDF
            </button>
          </div>

          {/* Key specs inset — hidden on mobile to surface products faster; visible md+ */}
          <div className="hidden md:block border border-paper/15 p-5 print:border-gray-300 print:block">
            <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3 print:text-ember">
              — Key specifications
            </p>
            <ul className="space-y-1.5 text-sm text-paper/80 print:text-gray-700">
              <li>
                <span className="text-paper-warm print:text-black font-medium">Material —</span>{' '}
                SAE 1075 high carbon steel, heat-treated for ideal balance between hardness and flexibility
              </li>
              <li>
                <span className="text-paper-warm print:text-black font-medium">Construction —</span>{' '}
                Full tang — steel runs the full length of the handle for maximum durability
              </li>
            </ul>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent print:hidden" />
      </header>

      {/* Catalog */}
      <div className="max-w-editorial mx-auto px-6 lg:px-10 py-16">
        {Object.entries(grouped).map(([category, products]) => (
          <section key={category} className="mb-20 print:break-inside-avoid">

            {/* Category header */}
            <div className="flex items-center gap-4 mb-8 pb-5 border-b border-ink-200">
              <div className="w-10 h-10 bg-ember text-paper-warm flex items-center justify-center print:bg-gray-800">
                <Package className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1">
                  — Category
                </p>
                <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-tight">
                  {category}.
                </h2>
              </div>
            </div>

            {/* Products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {products.map((product) => (
                <article
                  key={product.id}
                  className="border border-ink-200 hover:border-ink-300 transition-colors duration-300 ease-editorial print:break-inside-avoid print:hover:border-ink-200"
                >
                  {/* Product header */}
                  <div className="bg-paper-bright px-5 py-4 border-b border-ink-200 flex items-center gap-4">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-16 h-16 object-contain mix-blend-multiply"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-editorial italic text-xl text-ink-950 tracking-tight leading-tight">
                        {product.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 font-mono text-[10px] tracking-wider text-ink-500">
                        <span>{product.sku}</span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-1 h-1 bg-ink-300 rounded-full" />
                          {product.handleMaterial} handle
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-5">
                    {/* Description */}
                    <p className="text-sm text-ink-700 leading-[1.65] mb-5">{product.description}</p>

                    {/* Key Features */}
                    <div className="mb-5">
                      <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">
                        — Key features
                      </p>
                      <ul className="space-y-1.5 text-sm text-ink-900">
                        {product.pitch.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2.5">
                            <span className="text-ember mt-1 flex-shrink-0">—</span>
                            <span className="leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Specs */}
                    <div>
                      <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-3">
                        — Specifications
                      </p>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-ink-100">
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500 w-1/4">
                              Blade
                            </td>
                            <td className="py-2 pr-4 font-editorial italic text-ink-950 w-1/4">
                              {product.bladeLength}"
                            </td>
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500 w-1/4">
                              Weight
                            </td>
                            <td className="py-2 font-editorial italic text-ink-950 w-1/4">
                              {product.weight} lbs
                            </td>
                          </tr>
                          <tr className="border-b border-ink-100">
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                              Steel
                            </td>
                            <td className="py-2 pr-4 text-sm text-ink-900">{product.specs.steel}</td>
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                              Hardness
                            </td>
                            <td className="py-2 text-sm text-ink-900">{product.specs.hardness}</td>
                          </tr>
                          <tr>
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                              Geometry
                            </td>
                            <td className="py-2 pr-4 text-sm text-ink-900">{product.specs.geometry}</td>
                            <td className="py-2 pr-3 font-mono text-[10px] tracking-micro uppercase text-ink-500">
                              Handle
                            </td>
                            <td className="py-2 text-sm text-ink-900">{product.handleMaterial}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}

        {/* Footer note */}
        <div className="mt-12 p-8 bg-forge text-paper print:bg-gray-100 print:text-black">
          <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3 print:text-ember">
            — Ordering information
          </p>
          <h3 className="font-editorial italic text-2xl text-paper-warm mb-3 tracking-tight print:text-black">
            How to order.
          </h3>
          <p className="text-sm text-paper/70 leading-[1.65] print:text-gray-700">
            Buy any machete online — one blade or a dozen, in any combination. Just add what you
            want to your cart and check out. Wholesale pricing is available to businesses on request.
            All machetes feature SAE 1075 high carbon steel construction with heat treatment for
            optimal performance.
          </p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 0.5in; size: letter; }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default CatalogPage;
